import { useState, useEffect, useCallback } from "react";
import { Key, Plus, Trash2, Loader2, Copy, Check } from "lucide-react";
import api, { apiErr } from "../../lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "./growthConstants";

// Gestión de API keys para las automatizaciones (ChatGPT) — sección 23.1 del spec: el token
// completo solo se ve una vez, justo tras crearlo; a partir de ahí solo el prefijo.
export default function GrowthApiKeys() {
  const [keys, setKeys] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [justCreated, setJustCreated] = useState(null); // { key, keyPrefix } una sola vez
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    api.get("/growth/api-keys").then(({ data }) => setKeys(data.keys)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) { toast.warning("Ponle un nombre (ej. \"ChatGPT Growth Radar\")"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/growth/api-keys", { name: name.trim() });
      setJustCreated(data);
      setName("");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("¿Revocar esta API key? Dejará de funcionar de inmediato.")) return;
    try { await api.delete(`/growth/api-keys/${id}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(justCreated.key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="growth-api-keys">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Key size={16} /> API keys</h3>

      {justCreated && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <p className="mb-1.5 text-xs font-semibold text-amber-800">
            Copia esta key ahora — no se volverá a mostrar completa.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-white px-2 py-1.5 text-xs">{justCreated.key}</code>
            <button onClick={copyKey} className="shrink-0 rounded-lg border border-amber-300 p-1.5 text-amber-700 hover:bg-amber-100">
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        </div>
      )}

      <div className="mb-3 flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre (ej. ChatGPT Growth Radar)"
          data-testid="growth-api-key-name" className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm" />
        <button onClick={create} disabled={busy} data-testid="growth-api-key-create"
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Crear
        </button>
      </div>

      {!keys ? (
        <Loader2 className="animate-spin text-slate-300" size={18} />
      ) : keys.length === 0 ? (
        <p className="text-xs text-slate-400">Sin API keys todavía.</p>
      ) : (
        <div className="space-y-1.5">
          {keys.map((k) => (
            <div key={k.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 text-xs">
              <div className="min-w-0">
                <div className={`font-semibold ${k.revoked ? "text-slate-400 line-through" : "text-slate-700"}`}>{k.name}</div>
                <div className="text-slate-400">
                  {k.keyPrefix}… · creada {fmtDateTime(k.createdAt)}
                  {k.lastUsedAt && ` · usada ${fmtDateTime(k.lastUsedAt)}`}
                </div>
              </div>
              {!k.revoked && (
                <button onClick={() => revoke(k.id)} className="shrink-0 rounded-lg p-1.5 text-rose-500 hover:bg-rose-50">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
